import { createPayload } from '@/utils/createPayload';
import { reportSilentError } from '@/utils/storeErrorReporting';
import { API_DOMAIN, authHeaders, parseResponse } from '@/api/headers';

const DEFAULT_LIMIT = 12;
const DEFAULT_OFFSET = 0;

/**
 * Maps each argument of {@link fetchVolunteers} to its query param name on `GET /v2/volunteers`.
 */
const QUERY_PARAM_NAMES = {
    limit: 'limit',
    offset: 'offset',
    organizationId: 'organization_id',
    state: 'state',
    industryCode: 'industry_code',
    subcategoryId: 'subcategory_id',
    level: 'level',
    tenure: 'tenure',
    language: 'language',
    areasOfExpertise: 'areas_of_expertise',
    favourites: 'favourites',
    hasCareerShares: 'has_career_shares',
    preferredState: 'preferredState',
    preferredAreaOfExpertise: 'preferredAreaOfExpertise',
};

/**
 * Builds the query string; arrays are sent as repeated params, empty values are skipped.
 */
const buildQuery = (args) => {
    const params = new URLSearchParams();
    const isEmpty = (value) => value === undefined || value === null || value === '';

    for (const [argName, paramName] of Object.entries(QUERY_PARAM_NAMES)) {
        const values = [].concat(args[argName]);

        for (const value of values) {
            if (isEmpty(value)) {
                continue;
            }

            params.append(paramName, value);
        }
    }

    return params.toString();
};

/**
 * Fetches volunteers from `GET /v2/volunteers`, ordered by the BE's priority ranking.
 * Arguments are optional and positional; the query string is built here. "Repeatable" filters take a string or an
 * array of strings and match ANY of the values given.
 *
 * @param {number} [limit=12] - Number of items to return
 * @param {number} [offset=0] - Number of items to skip
 * @param {string|string[]} [organizationId] - Organization ID (repeatable)
 * @param {string|string[]} [state] - US state (repeatable)
 * @param {string} [industryCode] - Industry code
 * @param {string} [subcategoryId] - Sub-industry UUID
 * @param {string|string[]} [level] - Job level / workplace hierarchy (repeatable)
 * @param {string|string[]} [tenure] - Time at company (repeatable)
 * @param {string|string[]} [language] - Languages spoken (repeatable)
 * @param {string|string[]} [areasOfExpertise] - Areas of expertise (repeatable)
 * @param {boolean} [favourites] - Only volunteers the calling educator has favourited
 * @param {boolean} [hasCareerShares] - Only volunteers with at least one published career share
 * @param {string} [preferredState] - Boosts volunteers in the same state (low-quality profiles are not boosted)
 * @param {string|string[]} [preferredAreaOfExpertise] - Boosts volunteers sharing the area(s) of expertise (repeatable)
 * @returns {Promise<{records: Object[], total_records_count: number, offset: number, limit: number}|undefined>}
 *   Response body, or undefined if the request failed
 */
export const fetchVolunteers = async (
    limit,
    offset,
    organizationId,
    state,
    industryCode,
    subcategoryId,
    level,
    tenure,
    language,
    areasOfExpertise,
    favourites,
    hasCareerShares,
    preferredState,
    preferredAreaOfExpertise,
) => {
    try {
        const { token } = await createPayload(null);
        const query = buildQuery({
            limit: limit ?? DEFAULT_LIMIT,
            offset: offset ?? DEFAULT_OFFSET,
            organizationId,
            state,
            industryCode,
            subcategoryId,
            level,
            tenure,
            language,
            areasOfExpertise,
            favourites,
            hasCareerShares,
            preferredState,
            preferredAreaOfExpertise,
        });
        const endpointURL = `${API_DOMAIN}/v2/volunteers${query ? `?${query}` : ''}`;

        const response = await fetch(endpointURL, { method: 'GET', headers: authHeaders(token) });
        return await parseResponse(response);
    } catch (error) {
        reportSilentError(error);
        return undefined;
    }
};
